import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, Row, Col, Alert } from '@/components/ui';
import { previewCloneTaskName } from '../utils/cloneTaskPreview';
import { areasService, QuantifiedPlan } from '../services/areasService';
import { TaskStage } from '../types/Task';

export interface CloneTaskConfirmItem {
  taskId: number;
  sourceTaskLabel: string;
  sourceTaskName?: string | null;
  taskType?: string;
  completionAccomplishment?: number;
  contractYear?: string | null;
  serviceAreaId?: number | null;
}

export interface CloneQuantifiedOptions {
  contractYear: string;
  status: string;
}

interface CloneTaskConfirmModalProps {
  show: boolean;
  onHide: () => void;
  /** One name per item, same order as `items`. Empty string means use server default naming. */
  onConfirm: (names: string[], quantifiedOptions?: CloneQuantifiedOptions) => void;
  items: CloneTaskConfirmItem[];
}

const QUANTIFIED_CLONE_STATUSES: { value: TaskStage; label: string }[] = [
  { value: TaskStage.PROPOSED, label: 'Proposed' },
  { value: TaskStage.PLANNED, label: 'Planned' },
  { value: TaskStage.ACTIVE, label: 'Active' }
];

function defaultContractYearFromPlans(
  plans: QuantifiedPlan[],
  sourceContractYear?: string | null
): string {
  const openYears = Array.from(
    new Set(plans.filter((p) => p.status === 'Open').map((p) => p.year))
  ).sort((a, b) => b - a);

  const sourceMatch = sourceContractYear?.match(/\d+/);
  const sourceYear = sourceMatch ? Number(sourceMatch[0]) : NaN;
  if (Number.isFinite(sourceYear) && sourceYear > 0 && openYears.includes(sourceYear)) {
    return String(sourceYear);
  }

  const activeOpen = plans.find((p) => p.status === 'Open' && p.isActive);
  if (activeOpen && openYears.includes(activeOpen.year)) {
    return String(activeOpen.year);
  }

  if (openYears.length > 0) {
    return String(openYears[0]);
  }

  if (Number.isFinite(sourceYear) && sourceYear > 0) {
    return String(sourceYear);
  }

  return '';
}

/**
 * Confirm before clone: editable new task name(s); server default is the initial value.
 * Quantified tasks also collect contract year and status.
 */
const CloneTaskConfirmModal: React.FC<CloneTaskConfirmModalProps> = ({
  show,
  onHide,
  onConfirm,
  items
}) => {
  const isBulk = items.length > 1;
  const isQuantifiedClone =
    items.length > 0 && items.every((item) => item.taskType === 'Quantified');
  const primaryTask = items[0];
  const primaryTaskLabel = primaryTask
    ? `${primaryTask.sourceTaskLabel}${primaryTask.sourceTaskName ? ` - ${primaryTask.sourceTaskName}` : ''}`
    : '';
  const serviceAreaId = primaryTask?.serviceAreaId ?? null;

  const [nameDrafts, setNameDrafts] = useState<string[]>([]);
  const [contractYear, setContractYear] = useState('');
  const [status, setStatus] = useState<string>(TaskStage.PLANNED);
  const [quantifiedPlans, setQuantifiedPlans] = useState<QuantifiedPlan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(false);

  const itemsKey = items.map((i) => i.taskId).join(',');

  const hasZeroActualAccomplishment = items.some(
    (item) => (item.completionAccomplishment ?? 0) === 0
  );

  const { openContractYears, legacyContractYear } = useMemo(() => {
    const openYears = Array.from(
      new Set(quantifiedPlans.filter((p) => p.status === 'Open').map((p) => p.year))
    ).sort((a, b) => b - a);
    const cy = contractYear?.trim();
    const n = cy ? Number(cy) : NaN;
    const legacy =
      cy &&
      Number.isFinite(n) &&
      n > 0 &&
      !openYears.includes(n)
        ? {
            year: n,
            labelSuffix: quantifiedPlans.some((p) => p.year === n && p.status === 'Closed')
              ? '(Closed plan)'
              : '(current)'
          }
        : null;
    return { openContractYears: openYears, legacyContractYear: legacy };
  }, [quantifiedPlans, contractYear]);

  useLayoutEffect(() => {
    if (!show) return;
    setNameDrafts(
      items.map((it) => previewCloneTaskName(it.sourceTaskName, it.sourceTaskLabel))
    );
    setStatus(TaskStage.PLANNED);
  }, [show, itemsKey]);

  useEffect(() => {
    if (!show || !isQuantifiedClone || !serviceAreaId) {
      setQuantifiedPlans([]);
      return;
    }

    let cancelled = false;
    setLoadingPlans(true);
    areasService
      .getQuantifiedPlans(serviceAreaId)
      .then((plans) => {
        if (!cancelled) setQuantifiedPlans(plans);
      })
      .catch(() => {
        if (!cancelled) setQuantifiedPlans([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingPlans(false);
      });

    return () => {
      cancelled = true;
    };
  }, [show, isQuantifiedClone, serviceAreaId]);

  useEffect(() => {
    if (!show || !isQuantifiedClone) return;
    setContractYear(
      defaultContractYearFromPlans(quantifiedPlans, primaryTask?.contractYear)
    );
  }, [show, isQuantifiedClone, quantifiedPlans, primaryTask?.contractYear]);

  const handleConfirm = () => {
    const names = nameDrafts.map((n) => (n ?? '').trim());
    if (isQuantifiedClone) {
      onConfirm(names, {
        contractYear: contractYear.trim(),
        status
      });
      return;
    }
    onConfirm(names);
  };

  const canConfirmQuantified =
    !isQuantifiedClone || (contractYear.trim() !== '' && !loadingPlans);

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{isBulk ? 'Clone selected tasks?' : 'Clone this task?'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="tw-mb-3">
          {isBulk
            ? `You are about to create ${items.length} new tasks with the same setup as the originals. Submission history will not be copied.`
            : 'You are about to create a new task with the same setup as the original. Submission history will not be copied.'}
        </p>
        {!isBulk && primaryTaskLabel && (
          <Alert variant="warning" className="tw-py-2 tw-px-3">
            <div className="tw-text-sm tw-font-semibold">Task: {primaryTaskLabel}</div>
            <div className="tw-mb-0">Are you sure you want to clone this task?</div>
          </Alert>
        )}
        {isQuantifiedClone && hasZeroActualAccomplishment && (
          <Alert variant="warning" className="tw-py-2 tw-px-3 tw-mb-3">
            <div className="tw-text-sm tw-font-semibold">Zero actual accomplishment</div>
            <div className="tw-mb-0 tw-text-sm">
              {isBulk
                ? 'One or more selected tasks have no actual accomplishment. The cloned task(s) will be created with planned quantity and accomplishment set to 0.'
                : 'This task has no actual accomplishment. The cloned task will be created with planned quantity and accomplishment set to 0.'}
            </div>
          </Alert>
        )}
        {isQuantifiedClone && (
          <div className="tw-border tw-border-line tw-rounded tw-p-3 tw-bg-surface-alt tw-mb-3">
            <Form.Label className="tw-text-sm tw-text-content-muted tw-mb-2 tw-block">
              Quantified clone settings
            </Form.Label>
            <Row>
              <Col md={6}>
                <Form.Label className="tw-text-sm tw-mb-1">Quantified Year</Form.Label>
                <Form.Select
                  value={contractYear}
                  onChange={(e) => setContractYear(e.target.value)}
                  disabled={loadingPlans}
                  aria-label="Quantified year"
                >
                  <option value="">
                    {loadingPlans ? 'Loading years…' : 'Select quantified year'}
                  </option>
                  {!loadingPlans &&
                    openContractYears.map((year) => (
                      <option key={year} value={String(year)}>
                        {year}
                      </option>
                    ))}
                  {!loadingPlans && legacyContractYear && (
                    <option
                      key={`legacy-${legacyContractYear.year}`}
                      value={String(legacyContractYear.year)}
                    >
                      {legacyContractYear.year} {legacyContractYear.labelSuffix}
                    </option>
                  )}
                </Form.Select>
                {!loadingPlans &&
                  openContractYears.length === 0 &&
                  !legacyContractYear && (
                    <Form.Text className="tw-text-content-muted">
                      No open quantified plan found for this service area.
                    </Form.Text>
                  )}
              </Col>
              <Col md={6}>
                <Form.Label className="tw-text-sm tw-mb-1">Status</Form.Label>
                <Form.Select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  aria-label="Clone task status"
                >
                  {QUANTIFIED_CLONE_STATUSES.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Row>
            <div className="tw-text-sm tw-text-content-muted tw-mt-2 tw-mb-0">
              Planned quantity and accomplishment on the new task will be copied from this
              task&apos;s actual accomplishment.
            </div>
          </div>
        )}
        <div className="tw-border tw-border-line tw-rounded tw-p-3 tw-bg-surface-alt">
          <Form.Label className="tw-text-sm tw-text-content-muted tw-mb-2 tw-block">
            {isBulk ? 'New task names (you can edit each)' : 'New task name (you can edit)'}
          </Form.Label>
          <ul className="tw-list-none tw-pl-0 tw-mb-0">
            {items.map((item, idx) => {
              const isLast = idx === items.length - 1;
              return (
                <li
                  key={item.taskId}
                  className={isLast ? 'tw-mb-0' : 'tw-mb-3 tw-pb-3 tw-border-b tw-border-line'}
                >
                  {isBulk && (
                    <div className="tw-text-sm tw-text-content-muted tw-mb-1">
                      From: {item.sourceTaskLabel}
                      {item.sourceTaskName ? ` — ${item.sourceTaskName}` : ''}
                    </div>
                  )}
                  <Form.Control
                    type="text"
                    value={nameDrafts[idx] ?? ''}
                    onChange={(e) => {
                      const next = [...nameDrafts];
                      next[idx] = e.target.value;
                      setNameDrafts(next);
                    }}
                    maxLength={500}
                    aria-label={isBulk ? `New name for clone of ${item.sourceTaskLabel}` : 'New task name'}
                  />
                  {!isBulk && (
                    <div className="tw-text-sm tw-text-content-muted tw-mt-2">
                      From: {item.sourceTaskLabel}
                      {item.sourceTaskName ? ` — ${item.sourceTaskName}` : ''}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          <div className="tw-text-sm tw-text-content-muted tw-mt-2 tw-mb-0">
            Clear a name to use the automatic clone label (e.g. “… (clone)”). Max 500 characters.
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleConfirm} disabled={!canConfirmQuantified}>
          {isBulk ? `Clone ${items.length} tasks` : 'Clone task'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CloneTaskConfirmModal;
